import type { ReactNode } from "react";
import React, { Component } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, LogIn } from "lucide-react";
import { getTranslationsStatic } from "@/lib/i18n/LanguageContext";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, retryCount: 0 };
  }

  componentDidCatch(_error: Error, _errorInfo: React.ErrorInfo) {
    if (process.env.NODE_ENV === "development") {
      console.error("Auth Error Boundary caught an error:", _error, _errorInfo);
    }
  }

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: undefined,
      retryCount: prevState.retryCount + 1,
    }));

    // Force page reload if retried multiple times
    if (this.state.retryCount >= 2) {
      window.location.reload();
    }
  };

  handleLogin = () => {
    window.location.href = "/login";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const t = getTranslationsStatic();

      const isAuthError =
        this.state.error?.message?.includes("useAuth") ||
        this.state.error?.message?.includes("authentication") ||
        this.state.error?.message?.includes("AuthProvider");

      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <Alert variant="destructive" className="max-w-md">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {isAuthError ? t.auth.authenticationError : t.common.applicationError}
            </AlertTitle>
            <AlertDescription className="mt-4">
              <p className="mb-4">
                {isAuthError
                  ? t.auth.authSystemError
                  : this.state.error?.message || t.common.unexpectedError}
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={this.handleRetry}
                  disabled={this.state.retryCount >= 3}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {this.state.retryCount >= 2 ? t.common.reloadPage : t.common.tryAgain}
                </Button>

                {isAuthError && (
                  <Button variant="default" onClick={this.handleLogin}>
                    <LogIn className="mr-2 h-4 w-4" />
                    {t.auth.goToLogin}
                  </Button>
                )}
              </div>

              {this.state.retryCount > 0 && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {t.common.retryAttempt.replace("{n}", String(this.state.retryCount))}
                </p>
              )}
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
